import { useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, Drawer,
  FormControlLabel, IconButton, InputAdornment, MenuItem, Paper, Stack, Switch, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import { useQueryClient } from '@tanstack/react-query';
import { AnimatedNumber } from '@/components/AnimatedNumber';
import { Karta } from '@/components/Karta';
import { NaglowekEkranu } from '@/components/NaglowekEkranu';
import { ErrorState, LoadingState } from '@/components/states';
import type {
  GrafikKanonicznyOdpowiedz, GrafikPara, GrafikWynikZapisu, GrafikZmiana,
} from '@/lib/api/analytics';
import { useAuth } from '@/lib/auth/AuthContext';
import { formatCount } from '@/lib/format';
import {
  agregatMiasta, DNI_ROBOCZE, kolorTekstu, koloryRestauracji, licznikiSzkoly, maWeekend, NAZWA_DNIA,
  ocenRozklad, opisSasiednichDni, PROG_NIEROWNEGO_ROZKLADU, sasiednieDni, SKROT_DNIA, skrotyRestauracji,
  type ParaGrafiku,
} from '@/lib/grafik/analiza';
import { odswiezEkranyGrafiku, useAnalyticsApi, useGrafikAudyt, useGrafikKanoniczny } from '@/lib/useAnalytics';

/*
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * ADMINISTRACJA → GRAFIK — kanoniczny grafik szkół i restauracji (R5-15, OWNER 2026-10-04; od 2026-10-10
 * w Administracji, dawniej Restauracje → Grafik)
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * Komórka szkoła × dzień = restauracje dostępne w tej szkole tego dnia. To JEST reguła starego
 * panelu (zaznaczony dzień pary w `SchoolRestaurantAvailability`), nie nowa reguła.
 *
 * Źródło jest jedno: system główny, przez API starego panelu. Zapis wraca tam; odpowiedź serwera
 * po zapisie to stan odczytany ponownie ze źródła — ten sam, który stary panel pokaże po
 * odświeżeniu. Lokalnie pokazujemy zmianę od razu (licznik), ale ostatnie słowo ma serwer.
 *
 * Układ z arkusza OWNERA („Grafik Warszawa.xlsx”): szkoły w wierszach, dni jako bloki, restauracje
 * kolorami, liczba restauracji w dniu. Kolor to wyłącznie pomoc wzrokowa — tożsamością jest ID,
 * a nazwa i skrót są zawsze w tekście (dostępność).
 *
 * Ostrzeżenia (nierówny rozkład, sąsiednie dni) to WSPARCIE DECYZJI: niczego nie blokują
 * i niczego nie przestawiają.
 *
 * OD 2026-10-18 (OWNER): ten sam widok jest zakładką „Grafik wizualny” w Administracja → Szkoły i restauracje
 * (`osadzony` — bez własnego nagłówka ekranu). Godzina zamknięcia zamówień jest widoczna w siatce: przy szkole
 * (jedna godzina albo zakres) i w opisie każdego chipu restauracji.
 */

type Model = ReturnType<typeof zbudujModel>;

const klucz = (r: number, s: number) => `${r}:${s}`;

function zbudujModel(dane: GrafikKanonicznyOdpowiedz, nadpisania: ReadonlyMap<string, boolean[]>) {
  const pary: GrafikPara[] = dane.pary.map((p) => {
    const n = nadpisania.get(klucz(p.restauracja_id, p.szkola_id));
    return n ? { ...p, dni: n } : p;
  });
  const paryAnalizy: ParaGrafiku[] = pary.map((p) => ({ szkolaId: p.szkola_id, restauracjaId: p.restauracja_id, dni: p.dni }));
  const restauracje = new Map(dane.restauracje.map((r) => [r.id, r]));
  const szkoly = new Map(dane.szkoly.map((s) => [s.id, s]));
  const nazwyMiast = new Map(dane.miasta.map((m) => [m.id, m.nazwa]));

  // Restauracja → miasta, w których ma pary (do kolorów bez kolizji w mieście).
  const miastaRestauracji = new Map<number, Set<number>>();
  for (const p of pary) {
    const miasto = szkoly.get(p.szkola_id)?.miasto_id ?? -1;
    if (!miastaRestauracji.has(p.restauracja_id)) miastaRestauracji.set(p.restauracja_id, new Set());
    miastaRestauracji.get(p.restauracja_id)!.add(miasto);
  }
  const kolory = koloryRestauracji(miastaRestauracji);
  const skroty = skrotyRestauracji([...miastaRestauracji.keys()].map((id) => ({ id, nazwa: restauracje.get(id)?.nazwa ?? `restauracja ${id}` })));

  // Szkoły grafiku = szkoły mające choć jedną parę. Grupowanie po mieście z danych (bez listy na sztywno).
  const szkolyWGrafiku = [...new Set(pary.map((p) => p.szkola_id))];
  const wgMiasta = new Map<number, number[]>();
  for (const s of szkolyWGrafiku) {
    const m = szkoly.get(s)?.miasto_id ?? -1;
    if (!wgMiasta.has(m)) wgMiasta.set(m, []);
    wgMiasta.get(m)!.push(s);
  }
  const porownaj = (a: string, b: string) => a.localeCompare(b, 'pl');
  const miasta = [...wgMiasta.entries()]
    .map(([id, szkolyMiasta]) => {
      const paryMiasta = paryAnalizy.filter((p) => szkolyMiasta.includes(p.szkolaId));
      const wiersze = szkolyMiasta
        .map((sid) => {
          const liczniki = licznikiSzkoly(paryAnalizy, sid);
          const paryS = pary.filter((p) => p.szkola_id === sid);
          const sasiednie = paryS
            .map((p) => ({ restauracjaId: p.restauracja_id, pary: sasiednieDni(p.dni) }))
            .filter((x) => x.pary.length > 0);
          return {
            id: sid,
            nazwa: szkoly.get(sid)?.nazwa ?? `szkoła ${sid}`,
            nazwaZnana: szkoly.get(sid)?.nazwa_znana !== false,
            liczniki,
            rozklad: ocenRozklad(DNI_ROBOCZE.map((d) => liczniki[d] ?? 0)),
            sasiednie,
            pary: paryS,
          };
        })
        .sort((a, b) => porownaj(a.nazwa, b.nazwa));
      return {
        id,
        nazwa: id === -1 ? 'Bez miasta w systemie głównym' : nazwyMiast.get(id) ?? `Miasto ${id}`,
        agregat: agregatMiasta(paryAnalizy, szkolyMiasta),
        weekend: maWeekend(paryMiasta),
        restauracje: [...new Set(paryMiasta.map((p) => p.restauracjaId))]
          .sort((a, b) => porownaj(restauracje.get(a)?.nazwa ?? '', restauracje.get(b)?.nazwa ?? '')),
        wiersze,
      };
    })
    // Kolejność z DANYCH: najwięcej szkół najpierw (Warszawa, potem mniejsze miasta), remis → nazwa.
    .sort((a, b) => (a.id === -1 ? 1 : b.id === -1 ? -1 : b.wiersze.length - a.wiersze.length || porownaj(a.nazwa, b.nazwa)));

  const przypisaniaPonPt = paryAnalizy.reduce((acc, p) => acc + DNI_ROBOCZE.filter((d) => p.dni[d]).length, 0);
  // „Bez godziny” (znacznik przeniesiony ze starego „Administracja → Grafik”): para ma dni dostaw,
  // ale system główny nie podał czytelnej godziny zamknięcia zamówień. Nie zakładamy północy.
  const bezGodziny = pary.filter((p) => p.dni.some(Boolean) && !p.zamkniecie_zamowien);
  const ostrzezen = miasta.reduce((acc, m) => acc + m.wiersze.filter((w) => w.rozklad.nierowny || w.sasiednie.length > 0).length, 0);
  return {
    pary, restauracje, szkoly, kolory, skroty, miasta, przypisaniaPonPt, ostrzezen, bezGodziny,
    restauracjiAktywnych: new Set(paryAnalizy.filter((p) => p.dni.some(Boolean)).map((p) => p.restauracjaId)).size,
    szkolWGrafiku: szkolyWGrafiku.length,
  };
}

/** Godzina zamknięcia zamówień do opisu: „zamówienia do 10:55” albo jawne „bez godziny” (nigdy północ). */
function opisGodziny(godzina: string | null | undefined): string {
  return godzina ? `zamówienia do ${godzina}` : 'bez godziny zamknięcia zamówień';
}

/**
 * Godziny zamknięcia zamówień szkoły (pary z choć jednym dniem dostaw): jedna godzina, zakres albo „bez godziny”.
 * W systemie głównym godzina należy do pary szkoła–restauracja, więc w jednej szkole bywa kilka.
 */
function godzinySzkoly(pary: readonly GrafikPara[]): string | null {
  const aktywne = pary.filter((p) => p.dni.some(Boolean));
  if (!aktywne.length) return null;
  const znane = [...new Set(aktywne.map((p) => p.zamkniecie_zamowien).filter((g): g is string => !!g))].sort();
  const bez = aktywne.filter((p) => !p.zamkniecie_zamowien).length;
  const czesc = znane.length === 0 ? null : znane.length === 1 ? `zamówienia do ${znane[0]}` : `zamówienia do ${znane[0]}–${znane[znane.length - 1]}`;
  const brak = bez ? `bez godziny: ${bez}` : null;
  return [czesc, brak].filter(Boolean).join(' · ');
}

/** Mały kolorowy chip restauracji: skrót w tekście, pełna nazwa (i godzina zamówień pary) w `title` — lekko, bez setek tooltipów. */
function ChipRestauracji({ id, model, przygaszony = false, godzina }: {
  id: number; model: Model; przygaszony?: boolean; godzina?: string | null;
}) {
  const tlo = model.kolory.get(id) ?? '#b2b2b2';
  const nazwa = model.restauracje.get(id)?.nazwa ?? `restauracja ${id}`;
  const opis = godzina === undefined ? nazwa : `${nazwa} · ${opisGodziny(godzina)}`;
  return (
    <Box
      component="span"
      title={opis}
      aria-label={opis}
      data-restauracja-id={id}
      sx={{
        display: 'inline-flex', alignItems: 'center', px: 0.5, minWidth: 22, height: 18, borderRadius: '4px',
        fontSize: 10.5, fontWeight: 700, lineHeight: 1, letterSpacing: '0.02em',
        bgcolor: tlo, color: kolorTekstu(tlo), opacity: przygaszony ? 0.28 : 1,
        border: '1px solid rgba(25,25,35,0.12)',
      }}
    >
      {model.skroty.get(id) ?? '?'}
    </Box>
  );
}

function Legenda({ ids, model, onWybierz }: { ids: number[]; model: Model; onWybierz: (id: number) => void }) {
  return (
    <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }} aria-label="Legenda kolorów restauracji">
      <Typography variant="caption" sx={{ color: 'text.secondary', mr: 0.5 }}>Legenda:</Typography>
      {ids.map((id) => (
        <Button
          key={id}
          size="small"
          variant="text"
          onClick={() => onWybierz(id)}
          sx={{ px: 0.75, py: 0.25, minWidth: 0, gap: 0.75, color: 'text.primary', fontWeight: 600, fontSize: 12 }}
        >
          <ChipRestauracji id={id} model={model} />
          {model.restauracje.get(id)?.nazwa ?? `restauracja ${id}`}
        </Button>
      ))}
    </Stack>
  );
}

const komorkaSzkoly = {
  position: 'sticky', left: 0, zIndex: 2, bgcolor: 'background.paper', minWidth: { xs: 140, sm: 220 }, maxWidth: 280,
} as const;

export function GrafikRestauracjiPage({ osadzony = false }: { osadzony?: boolean } = {}) {
  const auth = useAuth();
  const mozeEdytowac = auth.has('schedule.write');
  const zapytanie = useGrafikKanoniczny();
  const api = useAnalyticsApi();
  const queryClient = useQueryClient();

  const [szukaj, setSzukaj] = useState('');
  const [filtrRestauracji, setFiltrRestauracji] = useState<number | ''>('');
  const [tylkoProblemy, setTylkoProblemy] = useState(false);
  const [zwiniete, setZwiniete] = useState<ReadonlySet<number>>(new Set());
  const [edycja, setEdycja] = useState<{ szkolaId: number; dzien: number } | null>(null);
  const [wybor, setWybor] = useState<Map<number, boolean>>(new Map());
  const [szczegol, setSzczegol] = useState<number | null>(null);
  const [nadpisania, setNadpisania] = useState<Map<string, boolean[]>>(new Map());
  const [zapisuje, setZapisuje] = useState(false);
  const [wynikZapisu, setWynikZapisu] = useState<{ ok: boolean; wyniki: GrafikWynikZapisu[]; blad?: string } | null>(null);
  const [pokazAudyt, setPokazAudyt] = useState(false);
  const audyt = useGrafikAudyt(pokazAudyt);

  const dane = zapytanie.data;
  const model = useMemo(() => (dane ? zbudujModel(dane, nadpisania) : null), [dane, nadpisania]);

  if (zapytanie.isLoading) return <LoadingState label="Wczytywanie grafiku z systemu głównego…" />;
  if (zapytanie.error || !dane || !model) {
    return (
      <Stack spacing={2}>
        {osadzony ? null : <NaglowekEkranu tytul="Grafik" />}
        <ErrorState
          error={zapytanie.error}
          context="Grafik czyta się wyłącznie z systemu głównego (to samo API co stary panel). Bez połączenia z nim nie pokazujemy kopii zastępczej."
        />
      </Stack>
    );
  }

  const fraza = szukaj.trim().toLocaleLowerCase('pl');
  const filtrujWiersz = (w: Model['miasta'][number]['wiersze'][number]) =>
    (!fraza || w.nazwa.toLocaleLowerCase('pl').includes(fraza))
    && (filtrRestauracji === '' || w.pary.some((p) => p.restauracja_id === filtrRestauracji))
    && (!tylkoProblemy || w.rozklad.nierowny || w.sasiednie.length > 0);
  const wszystkichWierszy = model.miasta.reduce((a, m) => a + m.wiersze.length, 0);
  const pokazanychWierszy = model.miasta.reduce((a, m) => a + m.wiersze.filter(filtrujWiersz).length, 0);
  const filtrAktywny = Boolean(fraza) || filtrRestauracji !== '' || tylkoProblemy;

  const parySzkoly = (szkolaId: number) => model.pary.filter((p) => p.szkola_id === szkolaId);

  function otworzEdycje(szkolaId: number, dzien: number) {
    const stan = new Map<number, boolean>();
    for (const p of parySzkoly(szkolaId)) stan.set(p.restauracja_id, p.dni[dzien] === true);
    setWybor(stan);
    setEdycja({ szkolaId, dzien });
  }

  async function zapisz() {
    if (!edycja || !dane) return;
    const kanon = new Map(dane.pary.map((p) => [klucz(p.restauracja_id, p.szkola_id), p]));
    const zmiany: GrafikZmiana[] = [];
    for (const [restauracjaId, zaznacz] of wybor) {
      const para = kanon.get(klucz(restauracjaId, edycja.szkolaId));
      if (!para) continue;
      const przed = para.dni[edycja.dzien] === true;
      if (przed !== zaznacz) zmiany.push({ szkola_id: edycja.szkolaId, restauracja_id: restauracjaId, dzien: edycja.dzien, zaznacz, przed });
    }
    if (!zmiany.length) { setEdycja(null); return; }
    // Licznik reaguje od razu; ostateczny stan przychodzi z serwera i z ponownego odczytu.
    const lokalne = new Map(nadpisania);
    for (const z of zmiany) {
      const para = kanon.get(klucz(z.restauracja_id, z.szkola_id))!;
      const dni = [...(lokalne.get(klucz(z.restauracja_id, z.szkola_id)) ?? para.dni)];
      dni[z.dzien] = z.zaznacz;
      lokalne.set(klucz(z.restauracja_id, z.szkola_id), dni);
    }
    setNadpisania(lokalne);
    setZapisuje(true);
    try {
      const odp = await api.grafikZmiany(zmiany);
      setWynikZapisu({ ok: odp.wszystkie_zapisane, wyniki: odp.wyniki });
    } catch (e) {
      setWynikZapisu({ ok: false, wyniki: [], blad: e instanceof Error ? e.message : 'Zapis nie powiódł się.' });
    } finally {
      setZapisuje(false);
      setEdycja(null);
      // Ten ekran i pozostałe ekrany CC na tym samym źródle (Administracja → Szkoły i restauracje).
      await odswiezEkranyGrafiku(queryClient);
      setNadpisania(new Map());
    }
  }

  const edytowanaSzkola = edycja ? model.szkoly.get(edycja.szkolaId) : null;
  const restauracjaSzczegolu = szczegol !== null ? model.restauracje.get(szczegol) : null;
  const paryRestauracji = szczegol !== null ? model.pary.filter((p) => p.restauracja_id === szczegol) : [];
  const weekendRestauracji = paryRestauracji.some((p) => p.dni[5] || p.dni[6]);

  return (
    <Stack spacing={3}>
      {osadzony ? (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Szkoły z kolorowymi skrótami restauracji, dzień po dniu. Kliknij dzień szkoły, żeby włączyć lub wyłączyć restauracje
          — zapis idzie do systemu głównego (to samo źródło co zakładka „Relacje”).
        </Typography>
      ) : (
        <NaglowekEkranu
          tytul="Grafik"
          opis={
            <>
              Która restauracja jest dostępna w której szkole w które dni. Źródło: system główny — to samo API co
              zakładka „Grafik” w starym panelu, więc zmiana tutaj jest zmianą tam.
            </>
          }
          kartaId="SCHEDULE-00"
        />
      )}

      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 2 }}>
        {[
          ['szkół w grafiku', model.szkolWGrafiku],
          ['restauracji z dostawami', model.restauracjiAktywnych],
          ['przypisań pon–pt', model.przypisaniaPonPt],
          ['szkół z ostrzeżeniem', model.ostrzezen],
        ].map(([etykieta, wartosc]) => (
          <Paper key={String(etykieta)} variant="outlined" sx={{ px: 2, py: 1.25, minWidth: 150 }}>
            <Typography className="sygnal-liczba" sx={{ fontSize: 26, fontWeight: 800, lineHeight: 1.1 }}>
              <AnimatedNumber value={Number(wartosc)} format={formatCount} />
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>{etykieta}</Typography>
          </Paper>
        ))}
      </Stack>

      {!dane.zapis_podlaczony ? (
        <Alert severity="info">
          Zapis grafiku nie jest w tym środowisku podłączony do systemu głównego — widok tylko do odczytu.
        </Alert>
      ) : !mozeEdytowac ? (
        <Alert severity="info">Twoje konto widzi grafik, ale go nie edytuje.</Alert>
      ) : null}

      {model.bezGodziny.length > 0 ? (
        <Alert severity="info" data-bez-godziny={model.bezGodziny.length}>
          W {model.bezGodziny.length} {model.bezGodziny.length === 1 ? 'parze' : 'parach'} z dniami dostaw nie ma zapisanej{' '}
          <strong>godziny zamknięcia zamówień</strong>. Nie przyjmujemy za nie północy — takie pary są oznaczone „bez godziny”.
        </Alert>
      ) : null}

      {wynikZapisu ? (
        <Alert severity={wynikZapisu.ok ? 'success' : 'warning'} onClose={() => setWynikZapisu(null)}>
          {wynikZapisu.blad
            ? `Zapis nie powiódł się: ${wynikZapisu.blad}`
            : wynikZapisu.ok
              ? `Zapisano w systemie głównym (${wynikZapisu.wyniki.filter((w) => w.stan === 'ZAPISANO').length}). Widok odczytany ponownie ze źródła.`
              : 'Część zmian NIE została zapisana — widok pokazuje aktualny stan systemu głównego:'}
          {!wynikZapisu.ok && wynikZapisu.wyniki.filter((w) => w.stan !== 'ZAPISANO' && w.stan !== 'BEZ_ZMIAN').map((w) => (
            <Box key={`${w.restauracja_id}:${w.szkola_id}`} component="div" sx={{ mt: 0.5 }}>
              {model.restauracje.get(w.restauracja_id)?.nazwa ?? w.restauracja_id}: {w.stan === 'KONFLIKT'
                ? `ktoś zmienił ten dzień w międzyczasie (${(w.dni_w_konflikcie ?? []).map((d) => SKROT_DNIA[d]).join(', ')}) — nic nie nadpisano`
                : w.powod ?? w.stan}
            </Box>
          ))}
        </Alert>
      ) : null}

      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
        <TextField
          size="small"
          label="Szukaj szkoły"
          value={szukaj}
          onChange={(e) => setSzukaj(e.target.value)}
          slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> } }}
          sx={{ minWidth: 220 }}
        />
        <TextField
          select
          size="small"
          label="Restauracja"
          value={filtrRestauracji}
          onChange={(e) => setFiltrRestauracji(e.target.value === '' ? '' : Number(e.target.value))}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="">Wszystkie</MenuItem>
          {[...model.restauracje.values()]
            .filter((r) => model.kolory.has(r.id))
            .sort((a, b) => a.nazwa.localeCompare(b.nazwa, 'pl'))
            .map((r) => <MenuItem key={r.id} value={r.id}>{r.nazwa}</MenuItem>)}
        </TextField>
        <FormControlLabel
          control={<Switch checked={tylkoProblemy} onChange={(e) => setTylkoProblemy(e.target.checked)} />}
          label="Tylko ostrzeżenia"
        />
        <Box sx={{ flexGrow: 1 }} />
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Odczytano {new Date(dane.odczytano).toLocaleTimeString('pl-PL', { timeZone: 'Europe/Warsaw' })}
        </Typography>
        <Button size="small" onClick={() => zapytanie.refetch()} disabled={zapytanie.isFetching}>Odśwież</Button>
      </Stack>

      {filtrAktywny ? (
        <Alert
          severity="info"
          action={<Button size="small" onClick={() => { setSzukaj(''); setFiltrRestauracji(''); setTylkoProblemy(false); }}>Wyczyść filtry</Button>}
        >
          Filtr: pokazano {pokazanychWierszy} z {wszystkichWierszy} szkół. Liczniki dni zawsze liczą WSZYSTKIE restauracje szkoły.
        </Alert>
      ) : null}

      {model.miasta.map((miasto) => {
        const dniKolumn = miasto.weekend ? [0, 1, 2, 3, 4, 5, 6] : [...DNI_ROBOCZE];
        const wiersze = miasto.wiersze.filter(filtrujWiersz);
        const zwinieta = zwiniete.has(miasto.id);
        if (filtrAktywny && wiersze.length === 0) return null;
        return (
          <Karta
            key={miasto.id}
            kartaId={`SCHEDULE-CITY-${miasto.id}`}
            nadtytul="Miasto"
            tytul={miasto.nazwa}
            akcja={
              <IconButton
                aria-label={zwinieta ? `Rozwiń ${miasto.nazwa}` : `Zwiń ${miasto.nazwa}`}
                onClick={() => setZwiniete((z) => { const n = new Set(z); if (n.has(miasto.id)) n.delete(miasto.id); else n.add(miasto.id); return n; })}
              >
                {zwinieta ? <ExpandMoreIcon /> : <ExpandLessIcon />}
              </IconButton>
            }
          >
            <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1, mb: 1.5 }} data-agregat-miasta={miasto.id}>
              <Chip size="small" label={`Szkół: ${miasto.agregat.szkol}`} />
              <Chip size="small" label={`Restauracji: ${miasto.agregat.restauracji}`} />
              {dniKolumn.map((d) => (
                <Chip key={d} size="small" variant="outlined" label={`${SKROT_DNIA[d]}: ${miasto.agregat.przypisaniaWDniu[d] ?? 0}`} />
              ))}
            </Stack>
            <Collapse in={!zwinieta} unmountOnExit>
              <Box sx={{ mb: 1.5 }}>
                <Legenda ids={miasto.restauracje} model={model} onWybierz={setSzczegol} />
              </Box>
              <TableContainer sx={{ maxHeight: '75vh', overflow: 'auto' }}>
                <Table stickyHeader size="small" aria-label={`Grafik — ${miasto.nazwa}`}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...komorkaSzkoly, zIndex: 3, fontWeight: 700 }}>Szkoła</TableCell>
                      {dniKolumn.map((d) => (
                        <TableCell key={d} align="center" sx={{ fontWeight: 700, minWidth: 120 }} title={NAZWA_DNIA[d]}>
                          {SKROT_DNIA[d]}
                        </TableCell>
                      ))}
                      <TableCell sx={{ fontWeight: 700, minWidth: 190 }}>Rozkład pon–pt</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {wiersze.map((w) => (
                      <TableRow key={w.id} hover data-szkola-id={w.id}>
                        <TableCell sx={{ ...komorkaSzkoly, fontWeight: 600 }}>
                          {w.nazwa}
                          {!w.nazwaZnana ? (
                            <Chip
                              size="small" label="?" variant="outlined" sx={{ ml: 0.5 }}
                              title="System główny nie podał nazwy tej szkoły. Wiersz zostaje — ukrycie zataiłoby realne dostawy."
                            />
                          ) : null}
                          {godzinySzkoly(w.pary) ? (
                            <Typography component="div" variant="caption" data-cutoff-szkoly={w.id}
                              sx={{ color: 'text.secondary', fontWeight: 500 }}>
                              {godzinySzkoly(w.pary)}
                            </Typography>
                          ) : null}
                        </TableCell>
                        {dniKolumn.map((d) => {
                          const restauracjeDnia = w.pary.filter((p) => p.dni[d]).map((p) => p.restauracja_id)
                            .sort((a, b) => (model.skroty.get(a) ?? '').localeCompare(model.skroty.get(b) ?? '', 'pl'));
                          const licznik = w.liczniki[d] ?? 0;
                          return (
                            <TableCell key={d} align="center" sx={{ p: 0.5, verticalAlign: 'top' }}>
                              <Box
                                component="button"
                                type="button"
                                onClick={() => otworzEdycje(w.id, d)}
                                aria-label={`${w.nazwa}, ${NAZWA_DNIA[d]}: ${licznik} restauracji${restauracjeDnia.length ? ` — ${restauracjeDnia.map((r) => model.restauracje.get(r)?.nazwa).join(', ')}` : ''}`}
                                data-dzien={d}
                                sx={{
                                  width: '100%', minHeight: 44, display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'center',
                                  justifyContent: 'space-between', border: '1px solid transparent', borderRadius: 1, bgcolor: 'transparent',
                                  cursor: 'pointer', p: 0.5, font: 'inherit',
                                  '&:hover': { borderColor: 'var(--linia-mocna)', bgcolor: 'var(--powierzchnia-2)' },
                                }}
                              >
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center' }}>
                                  {restauracjeDnia.map((r) => (
                                    <ChipRestauracji key={r} id={r} model={model} przygaszony={filtrRestauracji !== '' && r !== filtrRestauracji}
                                      godzina={w.pary.find((p) => p.restauracja_id === r)?.zamkniecie_zamowien ?? null} />
                                  ))}
                                </Box>
                                <Typography
                                  component="span"
                                  className="sygnal-liczba"
                                  data-licznik-dnia={licznik}
                                  sx={{ fontSize: 13, fontWeight: 800, color: licznik === 0 ? 'text.disabled' : 'text.primary' }}
                                >
                                  {licznik}
                                </Typography>
                              </Box>
                            </TableCell>
                          );
                        })}
                        <TableCell sx={{ verticalAlign: 'top' }}>
                          <Typography variant="caption" component="div" className="sygnal-liczba" sx={{ fontWeight: 600 }}>
                            śr. {w.rozklad.srednia.toLocaleString('pl-PL')} · min {w.rozklad.min} · max {w.rozklad.max} · rozpiętość {w.rozklad.rozpietosc}
                          </Typography>
                          {w.rozklad.nierowny ? (
                            <Chip
                              size="small"
                              color="warning"
                              variant="outlined"
                              sx={{ mt: 0.5 }}
                              data-ostrzezenie="rozklad"
                              label={`Nierówny rozkład: ${w.rozklad.min}–${w.rozklad.max} restauracji`}
                            />
                          ) : null}
                          {w.sasiednie.map((s) => (
                            <Box key={s.restauracjaId} component="div" data-ostrzezenie="sasiednie-dni" sx={{ mt: 0.5, display: 'flex', gap: 0.5, alignItems: 'center', fontSize: 12 }}>
                              <span aria-hidden="true">⚠</span>
                              <ChipRestauracji id={s.restauracjaId} model={model} />
                              <span>{model.restauracje.get(s.restauracjaId)?.nazwa}: {opisSasiednichDni(s.pary)}</span>
                            </Box>
                          ))}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Collapse>
          </Karta>
        );
      })}

      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        Ostrzeżenie o nierównym rozkładzie: rozpiętość liczby restauracji pon–pt ≥ {PROG_NIEROWNEGO_ROZKLADU}. Ostrzeżenie o sąsiednich
        dniach: restauracja 2–3 razy w tygodniu w dniach obok siebie (przy 4–5 dniach sąsiedztwa nie da się uniknąć). Oba są wsparciem
        decyzji — niczego nie blokują i niczego nie przestawiają.
      </Typography>

      <Box>
        <Button size="small" onClick={() => setPokazAudyt((v) => !v)}>
          {pokazAudyt ? 'Ukryj ostatnie zmiany' : 'Pokaż ostatnie zmiany grafiku'}
        </Button>
        <Collapse in={pokazAudyt} unmountOnExit>
          <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
            {audyt.data?.wpisy.length ? (
              <Table size="small" aria-label="Ostatnie zmiany grafiku">
                <TableHead>
                  <TableRow>
                    <TableCell>Kiedy</TableCell>
                    <TableCell>Kto</TableCell>
                    <TableCell>Szkoła</TableCell>
                    <TableCell>Restauracja</TableCell>
                    <TableCell>Dni</TableCell>
                    <TableCell>Wynik</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {audyt.data.wpisy.filter((x) => x.etap !== 'ZAMIAR').map((x, i) => (
                    <TableRow key={`${x.czas}-${i}`}>
                      <TableCell>{new Date(x.czas).toLocaleString('pl-PL', { timeZone: 'Europe/Warsaw' })}</TableCell>
                      <TableCell>{x.aktor ?? '—'}</TableCell>
                      <TableCell>{model.szkoly.get(x.szkola_id)?.nazwa ?? x.szkola_id}</TableCell>
                      <TableCell>{model.restauracje.get(x.restauracja_id)?.nazwa ?? x.restauracja_id}</TableCell>
                      <TableCell>
                        {x.dni_zmienione.map((d) => `${SKROT_DNIA[d]} ${x.przed[d] ? '✓' : '—'}→${x.po[d] ? '✓' : '—'}`).join(', ')}
                      </TableCell>
                      <TableCell>{x.etap}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>Brak zmian od uruchomienia usługi.</Typography>
            )}
            {audyt.data ? (
              <Typography variant="caption" component="div" sx={{ color: 'text.secondary', mt: 1 }}>{audyt.data.trwalosc}</Typography>
            ) : null}
          </Paper>
        </Collapse>
      </Box>

      {/* ── EDYCJA KOMÓRKI szkoła × dzień ─────────────────────────────────────────────────── */}
      <Dialog open={edycja !== null} onClose={() => (zapisuje ? undefined : setEdycja(null))} fullWidth maxWidth="sm">
        {edycja && edytowanaSzkola ? (
          <>
            <DialogTitle>
              Restauracje dostępne w tej szkole tego dnia
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {edytowanaSzkola.nazwa} — {NAZWA_DNIA[edycja.dzien]}
              </Typography>
            </DialogTitle>
            <DialogContent dividers>
              <Stack spacing={0.5}>
                {parySzkoly(edycja.szkolaId)
                  .sort((a, b) => (model.restauracje.get(a.restauracja_id)?.nazwa ?? '').localeCompare(model.restauracje.get(b.restauracja_id)?.nazwa ?? '', 'pl'))
                  .map((p) => (
                    <FormControlLabel
                      key={p.restauracja_id}
                      disabled={!mozeEdytowac || !dane.zapis_podlaczony || zapisuje}
                      control={
                        <Checkbox
                          checked={wybor.get(p.restauracja_id) === true}
                          onChange={(e) => setWybor((m) => new Map(m).set(p.restauracja_id, e.target.checked))}
                        />
                      }
                      label={
                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                          <ChipRestauracji id={p.restauracja_id} model={model} />
                          <span>{model.restauracje.get(p.restauracja_id)?.nazwa}</span>
                          <Typography component="span" variant="caption" sx={{ color: 'text.secondary' }}>
                            {p.zamkniecie_zamowien ? `zamówienia do ${p.zamkniecie_zamowien}` : 'bez godziny zamknięcia (nie zakładamy północy)'}
                          </Typography>
                        </Stack>
                      }
                    />
                  ))}
              </Stack>
              <Typography variant="caption" component="div" sx={{ color: 'text.secondary', mt: 1.5 }}>
                Lista obejmuje restauracje współpracujące z tą szkołą w systemie głównym. Dodanie restauracji do szkoły
                (nowa para) i odpięcie są w Administracja → Szkoły i restauracje. Godzina zamknięcia zamówień
                nie jest tu edytowana: zapis przenosi ją bez zmian.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setEdycja(null)} disabled={zapisuje}>Anuluj</Button>
              <Button
                variant="contained"
                onClick={() => void zapisz()}
                disabled={zapisuje || !mozeEdytowac || !dane.zapis_podlaczony}
              >
                {zapisuje ? 'Zapisywanie…' : 'Zapisz'}
              </Button>
            </DialogActions>
          </>
        ) : null}
      </Dialog>

      {/* ── SZCZEGÓŁ RESTAURACJI: szkoła × dni (jak stary panel) ─────────────────────────────── */}
      <Drawer anchor="right" open={szczegol !== null} onClose={() => setSzczegol(null)}>
        {szczegol !== null ? (
          <Box sx={{ width: { xs: '100vw', sm: 640 }, p: { xs: 2, sm: 3 } }} role="region" aria-label="Grafik restauracji">
            <Stack direction="row" sx={{ alignItems: 'center', gap: 1, mb: 2 }}>
              <ChipRestauracji id={szczegol} model={model} />
              <Typography variant="h6" component="h2" sx={{ flexGrow: 1 }}>{restauracjaSzczegolu?.nazwa}</Typography>
              <IconButton aria-label="Zamknij" onClick={() => setSzczegol(null)}><CloseIcon /></IconButton>
            </Stack>
            <TableContainer>
              <Table size="small" aria-label={`Szkoły i dni — ${restauracjaSzczegolu?.nazwa ?? ''}`}>
                <TableHead>
                  <TableRow>
                    <TableCell>Szkoła</TableCell>
                    {(weekendRestauracji ? [0, 1, 2, 3, 4, 5, 6] : [...DNI_ROBOCZE]).map((d) => (
                      <TableCell key={d} align="center" sx={{ px: 0.75 }}>{SKROT_DNIA[d]}</TableCell>
                    ))}
                    <TableCell>Zamówienia do</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paryRestauracji
                    .sort((a, b) => (model.szkoly.get(a.szkola_id)?.nazwa ?? '').localeCompare(model.szkoly.get(b.szkola_id)?.nazwa ?? '', 'pl'))
                    .map((p) => {
                      const s = sasiednieDni(p.dni);
                      return (
                        <TableRow key={p.szkola_id}>
                          <TableCell>
                            {model.szkoly.get(p.szkola_id)?.nazwa ?? p.szkola_id}
                            {s.length ? (
                              <Typography variant="caption" component="div" sx={{ color: 'warning.main' }}>⚠ {opisSasiednichDni(s)}</Typography>
                            ) : null}
                          </TableCell>
                          {(weekendRestauracji ? [0, 1, 2, 3, 4, 5, 6] : [...DNI_ROBOCZE]).map((d) => (
                            <TableCell key={d} align="center" sx={{ px: 0.75 }} aria-label={p.dni[d] ? 'tak' : 'nie'}>
                              {p.dni[d] ? '✓' : '—'}
                            </TableCell>
                          ))}
                          <TableCell>
                            {p.zamkniecie_zamowien ?? (
                              p.dni.some(Boolean)
                                ? <Chip size="small" color="warning" variant="outlined" label="bez godziny" title="Dostawy są, ale godziny zamknięcia zamówień nie znamy. Nie zakładamy północy." />
                                : '—'
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ) : null}
      </Drawer>
    </Stack>
  );
}

export default GrafikRestauracjiPage;
